import type { MechanicDefinition } from "./types";

export const encounterTables: MechanicDefinition = {
  id: "encounter_tables",
  name: "Events & Encounters",
  description: "Random encounters, world events, news broadcasts, environmental hazards, and story-driven occurrences that make the world feel alive",
  icon: "Shuffle",
  category: "core",
  aiHints: {
    purpose: "Generates random encounters, world news, and ambient events to make the world feel alive and dynamic.",
    whenToUse: "Open worlds, exploration-heavy games, or any world that should feel like it has things happening independently.",
    synergies: ["time_passage", "encounter_pacing", "reactive_world", "region_movement"],
    conflicts: [],
    prerequisites: [],
    tags: ["encounters", "random", "events", "news", "world-events", "ambient", "ripple"],
    complexity: 3,
    exampleConfig: { encounter_frequency: 4, world_news: true, story_events: true, timing_mode: "contextual" },
    stateVariables: ["_encounter_log", "_active_world_events", "_world_news_feed", "_event_ripples"],
  },
  configSchema: [
    {
      key: "timing_mode",
      label: "Event Timing Mode",
      type: "select",
      options: [
        { value: "contextual", label: "Contextual (recommended)", description: "Events happen at narrative seams — scene changes, time skips, location transitions, lulls. No rigid turn counting. Best for story-driven play." },
        { value: "turn_based", label: "Turn-Based (legacy)", description: "Classic fixed-interval turn counting. Events trigger every X turns regardless of narrative context." },
      ],
      default: "contextual",
    },
    {
      key: "encounter_frequency",
      label: "Event Frequency",
      type: "select",
      options: [
        { value: "rare", label: "Rare — sparse events", description: "Low density. Events feel special and impactful. Best for slow-burn campaigns." },
        { value: "moderate", label: "Moderate — regular events", description: "Standard density. Good mix of exploration and surprise." },
        { value: "frequent", label: "Frequent — dense events", description: "High density. The world constantly reacts and surprises." },
      ],
      default: "moderate",
    },
    {
      key: "encounter_types",
      label: "Event Categories",
      type: "text",
      default: "combat,social,exploration,treasure,hazard,world_event,news,merchant,rest,story_beat",
      description: "Comma-separated event categories. Add 'news' for world broadcasts, 'story_beat' for plot-advancing events.",
    },
    {
      key: "scaling",
      label: "Encounter Scaling",
      type: "boolean",
      default: true,
      description: "Scale encounter difficulty with player level.",
    },
    {
      key: "event_deck",
      label: "Event Deck Mode",
      type: "boolean",
      default: false,
      description: "Use a card-draw metaphor for events (no repeats until reshuffled).",
    },
    {
      key: "world_news",
      label: "World News & Broadcasts",
      type: "boolean",
      default: true,
      description: "Enable ambient world news delivered through lore-appropriate channels (radio, heralds, tavern gossip, data feeds). Makes the world feel alive with events happening beyond the player's view.",
    },
    {
      key: "news_frequency",
      label: "News Frequency",
      type: "select",
      options: [
        { value: "rare", label: "Rare — every 5-6 turns", description: "Occasional news bulletins. Only major events reach the player." },
        { value: "moderate", label: "Moderate — every 3-4 turns", description: "Regular news flow. Players stay informed about world events." },
        { value: "frequent", label: "Frequent — every 1-2 turns", description: "Constant information stream. News is everywhere — radio chatter, NPC gossip, bulletin boards." },
      ],
      default: "moderate",
    },
    {
      key: "news_channels",
      label: "News Delivery Channels",
      type: "text",
      default: "",
      description: "Comma-separated lore-friendly channels (e.g. 'radio,newspaper,town_crier,tavern_gossip,data_feed,crystal_ball'). Leave empty for auto-detection based on world setting.",
    },
    {
      key: "story_events",
      label: "Story-Driven Events",
      type: "boolean",
      default: true,
      description: "Events that advance or complicate the main plot, introduce new characters, or shift the world state. Not just random encounters — narrative catalysts.",
    },
    {
      key: "ambient_events",
      label: "Ambient World Events",
      type: "boolean",
      default: true,
      description: "Background events that don't directly involve the player but create a living world — festivals, market crashes, political shifts, natural phenomena.",
    },
    {
      key: "enable_ripple_planning",
      label: "Event Ripple Planning",
      type: "boolean",
      default: true,
      description: "When an event fires, automatically plan 2-4 future consequences at different time horizons (immediate, near-term, long-term). The AI weaves these ripples into the story organically.",
    },
    {
      key: "custom_events",
      label: "Custom Event Pool",
      type: "text",
      default: "",
      description: "Add specific events the AI should draw from (one per line). These are seeded into the event pool alongside generated ones.",
    },
  ],
  systemPromptSection: (config) => {
    const timingMode = config.timing_mode || "contextual";
    const freq = config.encounter_frequency || "moderate";
    const types = (config.encounter_types || "combat,social,exploration,treasure,hazard,world_event,news,merchant,rest,story_beat").split(",").map((s: string) => s.trim());
    const scaling = config.scaling !== false;
    const eventDeck = config.event_deck || false;
    const worldNews = config.world_news !== false;
    const newsFreq = config.news_frequency || "moderate";
    const newsChannels = config.news_channels ? config.news_channels.split(",").map((s: string) => s.trim()) : [];
    const storyEvents = config.story_events !== false;
    const ambientEvents = config.ambient_events !== false;
    const enableRipple = config.enable_ripple_planning !== false;
    const customEvents = config.custom_events ? config.custom_events.split("\n").filter((s: string) => s.trim()) : [];

    const contextualTimingBlock = `## EVENTS & ENCOUNTERS (Contextual Timing — Narrative-Driven)
- **DO NOT** count turns for event injection. Events happen at **narrative seams**:
  • Scene transitions (arriving somewhere new, leaving a conversation)
  • Location changes (entering a new area, region, building)
  • Time skips (montages, resting, travel narration, "weeks later")
  • Chapter breaks or natural story beats
  • Lulls in action (walking, waiting, downtime)
- Density: ${freq === "rare" ? "Low — only at major transitions. 1 event per 3-4 scenes." : freq === "frequent" ? "High — nearly every scene shift. The world buzzes constantly." : "Medium — every 2-3 scene transitions or whenever it feels natural."}
- Categories: ${types.join(", ")}
- **Subtle events** (news, ambient, gossip) can happen MID-SCENE — someone mentions something, a radio crackles, a poster is noticed.
- **Disruptive events** (combat, hazard, world_event) should only happen at clear narrative boundaries.
- Use your judgment: if the player is deep in an emotional conversation, don't interrupt with a bandit attack. Wait for the scene to breathe.
${scaling ? "- Encounters SCALE with player level." : ""}
${eventDeck ? "- EVENT DECK MODE: Track drawn events. No repeats until reshuffled." : ""}`;

    const turnBasedTimingBlock = `## EVENTS & ENCOUNTERS (Rules — Mandatory Post-Turn Check)
- Frequency: ${freq === "rare" ? "Roll every 3-4 turns (25%)" : freq === "frequent" ? "Roll nearly every turn (80%)" : "Roll every 2-3 turns (50%)"}
- Categories: ${types.join(", ")}
- MANDATORY: The postTurnHook tracks turns since last event and prompts you when it's time.
- Use roll_encounter for random encounters. Use resolve_encounter for outcomes. Use trigger_world_event for story/world events.
${scaling ? "- Encounters SCALE with player level." : ""}
${eventDeck ? "- EVENT DECK MODE: Track drawn events. No repeats until reshuffled." : ""}`;

    const rippleBlock = enableRipple ? `### EVENT RIPPLE SYSTEM
- When you use trigger_world_event or broadcast_world_news, the system automatically generates **future consequences** (ripples).
- Ripples are stored in state and fed to you as planned narrative beats.
- Each ripple has a timing: **immediate** (this scene or next), **near** (2-5 scenes), **far** (10+ scenes).
- When a ripple's timing feels right, use inject_ripple to mark it as woven into the story.
- Ripples are SUGGESTIONS, not mandates. Adapt them to current narrative context.
- Check _event_ripples in state — pending ripples whose timing aligns with the current narrative should be woven in.
- Don't force all ripples. If the story has moved past a ripple's relevance, skip or adapt it.` : "";

    return `${timingMode === "contextual" ? contextualTimingBlock : turnBasedTimingBlock}

### EVENT CATEGORIES
- **combat**: Direct confrontation — bandits, monsters, rival factions.
- **social**: NPC interactions — pleas for help, arguments, flirting, interrogation.
- **exploration**: Discovery — hidden passages, scenic vistas, ancient ruins.
- **treasure**: Loot — caches, abandoned supplies, reward drops.
- **hazard**: Environmental danger — rockslides, traps, toxic spills, storms.
- **world_event**: Major occurrence — political shift, natural disaster, faction war escalation.
- **news**: World information reaching the player through lore-appropriate channels.
- **story_beat**: Plot-advancing event — a clue surfaces, an ally appears, a villain acts.
- **merchant**: Trading opportunity — wandering trader, black market, caravan.

${storyEvents ? `### STORY-DRIVEN EVENTS
- Not all events are random. Some should ADVANCE or COMPLICATE the plot.
- Use trigger_world_event with category "story_beat" to introduce narrative catalysts.
- Story events should reference existing plot threads, entities, or factions.
- Examples: A villain's agent is spotted nearby, a quest-giver sends an urgent message, evidence of a conspiracy surfaces.` : ""}

${ambientEvents ? `### AMBIENT WORLD EVENTS
- The world moves without the player. Use trigger_world_event with category "ambient" periodically.
- These create atmosphere: festivals, elections, market fluctuations, weather phenomena, NPC gossip about distant events.
- Ambient events should be SPECIFIC to this world — reference actual locations, factions, NPCs from the codex.
- Players may choose to engage or ignore them.` : ""}

${worldNews ? `### WORLD NEWS & BROADCASTS
- Deliver world information through LORE-APPROPRIATE channels:
${newsChannels.length > 0 ? `  Channels: ${newsChannels.join(", ")}` : `  Auto-detect from world setting (fantasy: town criers, tavern gossip, messenger birds; sci-fi: radio, data feeds, holo-news; modern: TV, social media, newspapers; post-apocalyptic: radio broadcasts, trader rumors).`}
- News frequency: ${newsFreq === "rare" ? "every 5-6 scenes" : newsFreq === "frequent" ? "every 1-2 scenes" : "every 3-4 scenes"}
- Use broadcast_world_news to deliver news. The tool creates an immersive in-world delivery.
- News should include:
  - Events happening elsewhere in the world (battles, discoveries, political changes)
  - Rumors that may or may not be true (creates intrigue)
  - Information that could become quest hooks
  - Updates on faction activities the player has interacted with
  - Weather warnings, trade route changes, bounty postings
- News MUST feel organic — an NPC casually mentioning something, overhearing a conversation, finding a posted notice.
- NEVER break immersion with meta-style news. It's always delivered by a character, device, or environment.` : ""}

${rippleBlock}

${customEvents.length > 0 ? `### CUSTOM EVENT POOL (Builder-defined)
${customEvents.map((e, i) => `${i + 1}. ${e}`).join("\n")}
- Draw from these when appropriate. They take priority over random generation.` : ""}

### ENCOUNTER TABLE (1d100)
**Wilderness:** 01-25 Combat, 26-40 Exploration, 41-50 Social, 51-60 Hazard, 61-70 Treasure, 71-80 World Event, 81-88 News, 89-95 Story Beat, 96-100 Merchant
**Dungeon:** 01-35 Combat, 36-50 Trap, 51-60 Treasure, 61-70 Puzzle, 71-80 Lore, 81-90 Ambush, 91-95 Story Beat, 96-100 Boss
**Town:** 01-15 Social, 16-30 News, 31-45 Merchant, 46-55 Quest Hook, 56-65 World Event, 66-75 Story Beat, 76-85 Gossip, 86-100 Festival/Event

### SEVERITY (region difficulty 1-10)
- 1-3: Trivial-Easy. 4-6: Medium. 7-8: Hard. 9-10: Deadly.`;
  },
  initializeState: (config) => ({
    turns_since_encounter: 0,
    turns_since_news: 0,
    beats_since_encounter: 0,
    beats_since_news: 0,
    total_encounters: 0,
    total_events: 0,
    encounter_history: [],
    world_news_log: [],
    event_deck_drawn: config.event_deck ? [] : undefined,
    active_world_events: [],
    _event_ripples: [],
  }),
  onEvent: (event) => {
    // Narrative beat tracking for contextual timing
    if (event.type === "scene_transition" || event.type === "location_change" || event.type === "time_skip" || event.type === "chapter_break") {
      return {
        narrativeNotes: [`🎭 Narrative seam detected (${event.type}) — good opportunity for an event, news, or ambient occurrence.`],
        stateUpdates: { beats_since_encounter: (event.data?.beats_since_encounter || 0) + 1, beats_since_news: (event.data?.beats_since_news || 0) + 1 },
      };
    }
    if (event.type === "region_enter") {
      return {
        narrativeNotes: ["🎲 New region entered — event check pending."],
        stateUpdates: { turns_since_encounter: 0, beats_since_encounter: 0 },
      };
    }
    if (event.type === "world_event_triggered") {
      return {
        narrativeNotes: [`🌍 World event "${event.data.title}" is active — may affect NPC behavior and available options.`],
      };
    }
    return null;
  },
  postTurnHook: (context) => {
    const timingMode = context.config.timing_mode || "contextual";
    const freq = context.config.encounter_frequency || "moderate";
    const notes: string[] = [];
    const updates: Record<string, any> = {};

    if (timingMode === "contextual") {
      // Contextual mode: gentle nudges based on beat count, not hard triggers
      const beats = context.state.variables?.beats_since_encounter ?? 0;
      const beatThreshold = freq === "rare" ? 4 : freq === "frequent" ? 1 : 2;

      if (beats >= beatThreshold) {
        notes.push(`🎭 ${beats} narrative beats since last event (density: ${freq}). Consider weaving in an event, news item, or ambient occurrence at the next narrative seam. Use trigger_world_event, broadcast_world_news, or roll_encounter when the moment feels right.`);
      } else {
        updates.beats_since_encounter = beats + 1;
      }

      // News nudge
      if (context.config.world_news !== false) {
        const newsBeats = context.state.variables?.beats_since_news ?? 0;
        const newsThreshold = (context.config.news_frequency || "moderate") === "rare" ? 6 : (context.config.news_frequency || "moderate") === "frequent" ? 1 : 3;
        if (newsBeats >= newsThreshold) {
          notes.push(`📰 ${newsBeats} beats since last news. Consider delivering world news organically if there's a natural moment.`);
        } else {
          updates.beats_since_news = newsBeats + 1;
        }
      }
    } else {
      // Legacy turn-based mode
      const current = context.state.variables?.turns_since_encounter ?? 0;
      const threshold = freq === "rare" ? 3 : freq === "frequent" ? 1 : 2;

      if (current >= threshold) {
        notes.push(`🎲 EVENT CHECK: ${current + 1} turns since last event (threshold: ${threshold + 1}). You SHOULD call roll_encounter, trigger_world_event, or broadcast_world_news now.`);
      } else {
        updates.turns_since_encounter = current + 1;
      }

      if (context.config.world_news !== false) {
        const newsFreq = context.config.news_frequency || "moderate";
        const turnsSinceNews = context.state.variables?.turns_since_news ?? 0;
        const newsThreshold = newsFreq === "rare" ? 5 : newsFreq === "frequent" ? 1 : 3;
        if (turnsSinceNews >= newsThreshold) {
          notes.push(`📰 NEWS CHECK: ${turnsSinceNews + 1} turns since last world news. Use broadcast_world_news to deliver lore-appropriate news to the player.`);
        } else {
          updates.turns_since_news = turnsSinceNews + 1;
        }
      }
    }

    // Pending ripple reminders
    const ripples = context.state.variables?._event_ripples || [];
    const pendingRipples = ripples.filter((r: any) => !r.injected);
    if (pendingRipples.length > 0) {
      const immediateRipples = pendingRipples.filter((r: any) => r.timing === "immediate");
      const nearRipples = pendingRipples.filter((r: any) => r.timing === "near");
      if (immediateRipples.length > 0) {
        notes.push(`⚡ IMMEDIATE ripples pending: ${immediateRipples.map((r: any) => `"${r.description}"`).join(", ")}. Weave these into the current or next scene.`);
      }
      if (nearRipples.length > 0) {
        notes.push(`🔮 Near-term ripples queued (${nearRipples.length}): ${nearRipples.map((r: any) => `"${r.description}"`).join(", ")}. Look for opportunities to introduce these.`);
      }
    }

    // Active world events reminder
    const activeEvents = context.state.variables?.active_world_events || [];
    if (activeEvents.length > 0) {
      const eventList = activeEvents.filter((e: any) => !e.resolved).map((e: any) => e.title || e.id).join(", ");
      if (eventList) {
        notes.push(`🌍 Active world events: ${eventList}. These should influence NPC behavior and available options.`);
      }
    }

    return {
      ...(Object.keys(updates).length > 0 ? { stateUpdates: updates } : {}),
      ...(notes.length > 0 ? { narrativeNotes: notes } : {}),
    };
  },
  widgets: [
    {
      id: "encounter_log",
      type: "sidebar_tab",
      label: "Events",
      icon: "Shuffle",
      component: "EncounterLogWidget",
      dataBindings: {
        encounter_history: "variables.encounter_history",
        world_news_log: "variables.world_news_log",
        active_world_events: "variables.active_world_events",
        event_ripples: "variables._event_ripples",
        turns_since_encounter: "variables.turns_since_encounter",
        beats_since_encounter: "variables.beats_since_encounter",
        turns_since_news: "variables.turns_since_news",
        total_encounters: "variables.total_encounters",
        total_events: "variables.total_events",
      },
      visibility: "always",
      priority: 50,
    },
  ],
  tools: [
    {
      type: "function",
      function: {
        name: "roll_encounter",
        description: "Roll on the encounter table for the current region. Generates a random encounter based on region type and difficulty.",
        parameters: {
          type: "object",
          properties: {
            region_type: { type: "string", description: "Type of region (wilderness, dungeon, town, etc.)" },
            difficulty: { type: "number", description: "Region difficulty 1-10" },
            player_level: { type: "number", description: "Current player level for scaling" },
            modifier: { type: "number", description: "Modifier to encounter roll" },
            forced_type: { type: "string", description: "Force specific encounter type" },
          },
          required: ["region_type"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "resolve_encounter",
        description: "Resolve any event or encounter outcome — combat, social, story, or world event.",
        parameters: {
          type: "object",
          properties: {
            encounter_type: { type: "string", description: "Type: combat, social, story_beat, world_event, news, etc." },
            player_action: { type: "string", description: "What the player did" },
            outcome: { type: "string", description: "success, partial, failure, fled, ignored" },
            rewards: { type: "string", description: "Any rewards gained" },
            consequences: { type: "string", description: "Narrative consequences" },
            affects_plot: { type: "boolean", description: "Whether this outcome affects the main plot" },
          },
          required: ["encounter_type", "outcome"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "trigger_world_event",
        description: "Trigger a world event that affects the broader game world — political shifts, natural disasters, faction actions, story beats. These events persist and influence NPC behavior. Automatically generates ripple consequences if ripple planning is enabled.",
        parameters: {
          type: "object",
          properties: {
            title: { type: "string", description: "Short event title" },
            description: { type: "string", description: "What happened in the world" },
            category: { type: "string", description: "world_event, story_beat, ambient, faction, disaster, political, economic" },
            severity: { type: "string", description: "minor, moderate, major, catastrophic" },
            affected_factions: { type: "array", items: { type: "string" }, description: "Factions affected by this event" },
            affected_locations: { type: "array", items: { type: "string" }, description: "Locations affected" },
            duration: { type: "string", description: "How long the event persists: instant, short (2-3 turns), medium (5-10 turns), long (20+ turns), permanent" },
            player_can_intervene: { type: "boolean", description: "Whether the player can affect this event's outcome" },
          },
          required: ["title", "description", "category"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "broadcast_world_news",
        description: "Deliver world news through a lore-appropriate channel. The news should feel organic — overheard conversation, radio broadcast, posted notice, messenger, data feed, etc. Automatically generates ripple consequences if ripple planning is enabled.",
        parameters: {
          type: "object",
          properties: {
            headline: { type: "string", description: "The news headline or core information" },
            details: { type: "string", description: "Additional context or details" },
            channel: { type: "string", description: "How the news is delivered (radio, tavern_gossip, town_crier, newspaper, data_feed, messenger, overheard, posted_notice, npc_conversation)" },
            source_name: { type: "string", description: "Who/what is delivering the news (NPC name, radio station, newspaper name)" },
            reliability: { type: "string", description: "How reliable is this news: confirmed, likely, rumor, propaganda, misinformation" },
            is_relevant_to_player: { type: "boolean", description: "Whether this directly affects the player's current situation or quests" },
            could_be_quest_hook: { type: "boolean", description: "Whether this news could lead to a new quest or opportunity" },
          },
          required: ["headline", "channel"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "draw_event_card",
        description: "Draw a random event from the event deck (no repeats until reshuffled).",
        parameters: {
          type: "object",
          properties: {
            deck_type: { type: "string", description: "Event deck category" },
            reshuffle: { type: "boolean", description: "Force reshuffle" },
          },
          required: ["deck_type"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "resolve_world_event",
        description: "Mark a world event as resolved or escalated. Updates the active events list.",
        parameters: {
          type: "object",
          properties: {
            event_title: { type: "string", description: "Title of the world event to resolve" },
            resolution: { type: "string", description: "How it was resolved" },
            escalate: { type: "boolean", description: "If true, event escalates rather than resolves" },
            new_consequences: { type: "string", description: "What happens as a result" },
          },
          required: ["event_title", "resolution"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "inject_ripple",
        description: "Mark an event ripple as woven into the story. Call this when you narratively incorporate a pending ripple consequence. This tracks which ripples have been addressed.",
        parameters: {
          type: "object",
          properties: {
            ripple_index: { type: "number", description: "Index of the ripple in _event_ripples array" },
            narrative_note: { type: "string", description: "Brief description of how the ripple was woven into the story" },
          },
          required: ["ripple_index"],
        },
      },
    },
  ],
};
